import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MaterialIcon from './MaterialIcon';
import { mediaUrl } from '../utils/media';

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

export default function Header({ onMenu, searchPlaceholder = 'Search parameters...' }) {
  const navigate = useNavigate();
  const user = useMemo(readStoredUser, []);
  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    'Administrator';
  const initial = displayName.charAt(0).toUpperCase() || 'A';
  const avatarUrl = mediaUrl(user?.profile?.avatarUrl || '');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login', { replace: true });
  };

  const quickActions = [
    { icon: 'notifications', label: 'Open notifications', onClick: () => navigate('/notifications') },
    { icon: 'notifications', label: 'Open command alerts', onClick: () => navigate('/command-alerts') },
    { icon: 'settings', label: 'Open systems', onClick: () => navigate('/systems') },
    { icon: 'help', label: 'Open support queue', onClick: () => navigate('/support') },
  ];

  return (
    <header className="fixed left-0 right-0 top-0 z-50 h-16 border-b border-[#4e4632] bg-[#1c1b1b]/95 backdrop-blur-md lg:left-64">
      <div className="flex h-full items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-3 lg:gap-8">
          <button
            type="button"
            onClick={onMenu}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#d2c5ab] transition hover:bg-[#2a2a2a] lg:hidden"
            aria-label="Open navigation"
          >
            <MaterialIcon name="menu" className="text-xl" />
          </button>

          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="shrink-0 font-['Geist'] text-xl font-black tracking-[-0.04em] text-[#ffedc3]"
          >
            SHIMBIRR COMMAND
          </button>

          <label className="relative hidden lg:block">
            <span className="absolute inset-y-0 left-3 flex items-center text-[#8f866e]">
              <MaterialIcon name="search" className="text-lg" />
            </span>
            <input
              readOnly
              placeholder={searchPlaceholder}
              className="w-64 rounded-lg border border-[#4e4632] bg-[#2a2a2a] py-2 pl-10 pr-4 text-sm text-[#e5e2e1] outline-none placeholder:text-[#8f866e]"
            />
          </label>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {quickActions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={action.onClick}
              aria-label={action.label}
              title={action.label}
              className="rounded-full p-2 text-[#d2c5ab] transition hover:bg-[#2a2a2a] hover:text-[#ffedc3]"
            >
              <MaterialIcon name={action.icon} className="text-xl" />
            </button>
          ))}

          <div className="hidden h-8 w-px bg-[#4e4632] lg:block" />

          <button
            type="button"
            onClick={handleLogout}
            className="hidden rounded-lg px-3 py-2 text-sm font-bold text-[#ffcc00] transition hover:bg-[#2a2a2a] lg:block"
          >
            LOGOUT
          </button>

          <div className="flex items-center gap-3">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={displayName}
                className="h-8 w-8 rounded-full border border-[#ffcc00] object-cover grayscale"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full border border-[#ffcc00] bg-[#201f1f] text-sm font-bold text-[#ffedc3]">
                {initial}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
